import { useQuery } from "@tanstack/react-query";
import api from "../../http";
import { useForBindingId } from '../../../forBinding';
import { Sector } from '../../types';

interface RawSectorDoc extends Omit<Sector, 'propName' | 'areaName'> {
    prop?: { name?: string };
    area?: { name?: string };
}

interface RawSectorsResponse {
    docs: RawSectorDoc[];
    totalDocs: number;
    limit: number;
    page: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
}

interface UseGetSectorsParams {
    page?: number;
    limit?: number;
    filter?: string;
    areaId?: string | string[];
    sortAsc?: boolean;
    enabled?: boolean;
    fetchAllPages?: boolean;
}

export function useGetSectors({
    page = 1,
    limit = 100,
    filter,
    areaId,
    sortAsc,
    enabled = true,
    fetchAllPages = false
}: UseGetSectorsParams = {}) {
    const forBinding = useForBindingId();
    const query = useQuery({
        queryKey: ['sectors', forBinding, page, limit, filter, areaId, sortAsc, fetchAllPages],
        enabled,
        queryFn: async () => {
            const fetchPage = async (requestedPage: number) => {
                const params = new URLSearchParams();
                params.set('page', String(requestedPage));
                params.set('limit', String(limit));

                if (forBinding) params.set('forBinding', forBinding);
                if (filter) params.set('filter', filter);
                if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));

                const areaIds = Array.isArray(areaId) ? areaId : areaId ? [areaId] : [];
                areaIds.forEach((id) => params.append('areaId', id));

                const { data } = await api.get<RawSectorsResponse>('/user/prop/sector/findAll', {
                    params
                });
                return data;
            };

            const firstPage = await fetchPage(fetchAllPages ? 1 : page);
            const remainingPages = fetchAllPages && firstPage.totalPages > 1
                ? await Promise.all(
                    Array.from({ length: firstPage.totalPages - 1 }, (_, index) => fetchPage(index + 2))
                )
                : [];
            const docs = [firstPage, ...remainingPages].flatMap((result) => result.docs);
            const data = {
                ...firstPage,
                docs,
                ...(fetchAllPages ? {
                    limit: docs.length,
                    page: 1,
                    totalPages: 1,
                    hasPrevPage: false,
                    hasNextPage: false,
                    prevPage: null,
                    nextPage: null,
                } : {}),
            };

            return {
                ...data,
                docs: data.docs.map((d): Sector => ({
                    ...d,
                    propName: d.prop?.name ?? '',
                    areaName: d.area?.name ?? '',
                })),
            };
        }
    });

    return {
        ...query,
        data: query.data?.docs ?? [],
        pagination: query.data ? {
            totalDocs: query.data.totalDocs,
            totalPages: query.data.totalPages,
            page: query.data.page,
            hasPrevPage: query.data.hasPrevPage,
            hasNextPage: query.data.hasNextPage,
            prevPage: query.data.prevPage,
            nextPage: query.data.nextPage,
        } : null,
    };
}
